'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';

import { loadInFlightPayment } from '../lib/adyenRedirectSession';

export type PaymentReturnGateway = 'adyen' | null;

export function usePaymentReturn() {
  const searchParams = useSearchParams();
  const cleaned = useRef(false);

  const donationId = searchParams.get('donationId');
  const redirectResult = searchParams.get('redirectResult');
  const sessionId = searchParams.get('sessionId');
  const eventId = searchParams.get('eventId');
  const campaignId = searchParams.get('campaignId');
  const recoveryToken = searchParams.get('recoveryToken');

  // Detection: Adyen sets redirectResult
  const isAdyenRedirectReturn = !!redirectResult && !!donationId;
  const isUrlRedirectReturn = isAdyenRedirectReturn;

  // localStorage fallback — detects in-flight payments after native app 3DS returns
  // that kill the browser tab (no URL params, sessionStorage wiped)
  const inFlightPayment = useMemo(() => {
    if (isUrlRedirectReturn) return null; // URL params take priority
    return loadInFlightPayment();
  }, [isUrlRedirectReturn]);

  const isRedirectReturn = isUrlRedirectReturn || !!inFlightPayment;

  const gateway: PaymentReturnGateway = isAdyenRedirectReturn
    ? 'adyen'
    : inFlightPayment
      ? 'adyen'
      : null;

  // Capture values in state so they survive URL cleanup
  const [captured] = useState(() => {
    if (isUrlRedirectReturn) {
      return {
        donationId,
        redirectResult,
        sessionId,
        eventId,
        campaignId,
        recoveryToken,
        gateway,
        isLocalStorageRecovery: false,
      };
    }
    if (inFlightPayment) {
      return {
        donationId: inFlightPayment.donationId,
        redirectResult: null,
        sessionId: inFlightPayment.sessionId,
        eventId: null,
        campaignId: null,
        recoveryToken: null,
        gateway: 'adyen' as PaymentReturnGateway,
        isLocalStorageRecovery: true,
      };
    }
    return null;
  });

  // Clean up URL params after reading them (once)
  useEffect(() => {
    if (isUrlRedirectReturn && !cleaned.current) {
      cleaned.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete('donationId');
      url.searchParams.delete('redirectResult');
      url.searchParams.delete('sessionId');
      url.searchParams.delete('eventId');
      url.searchParams.delete('campaignId');
      url.searchParams.delete('recoveryToken');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [isUrlRedirectReturn]);

  return {
    returnDonationId: captured?.donationId ?? null,
    isRedirectReturn: !!captured,
    isLocalStorageRecovery: captured?.isLocalStorageRecovery ?? false,
    gateway: captured?.gateway ?? null,
    // Adyen
    redirectResult: captured?.redirectResult ?? null,
    sessionId: captured?.sessionId ?? null,
    // Shared
    eventId: captured?.eventId ?? null,
    campaignId: captured?.campaignId ?? null,
    recoveryToken: captured?.recoveryToken ?? null,
  };
}
